import { c as _c } from "react/compiler-runtime";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { type OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { LANDING_HERO_ORBIT_TUNING } from "#/features/homePage/lib/landingHeroOrbitMotionConstants";
import { useMobileLayout } from "#/shared/hooks";
import { prefersReducedMotion } from "#/shared/lib/prefersReducedMotion";
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const TOUCH_DELTA_EPSILON = 0.0004;
export type UseHeroOrbitModelMotionParams = {
  controlsRef: RefObject<ThreeOrbitControls | null>;
  baseAzimuth: number;
  basePolar: number;
  /** Flip horizontal pointer mapping (e.g. left-side decoration). */
  invertPointerX?: boolean;
  /** Seconds offset into idle motion so paired models do not move in phase. */
  idleMotionPhaseSec?: number;
};

/**
 * Mobile: idle oscillation + touch parallax from screen-space finger position (window touches).
 * Desktop: pointer parallax only. No scroll-driven motion.
 */
export const useHeroOrbitModelMotion = t0 => {
  const $ = _c(36);
  const {
    controlsRef,
    baseAzimuth,
    basePolar,
    invertPointerX: t1,
    idleMotionPhaseSec: t2
  } = t0;
  const invertPointerX = t1 === undefined ? false : t1;
  const idleMotionPhaseSec = t2 === undefined ? 0 : t2;
  const isMobileLayout = useMobileLayout();
  const currentAzimuthRef = useRef(baseAzimuth);
  const currentPolarRef = useRef(basePolar);
  const targetAzimuthRef = useRef(baseAzimuth);
  const targetPolarRef = useRef(basePolar);
  const pointerAzimuthDeltaRef = useRef(0);
  const pointerPolarDeltaRef = useRef(0);
  const touchAzimuthDeltaRef = useRef(0);
  const touchPolarDeltaRef = useRef(0);
  const idleAzimuthSmoothedRef = useRef(0);
  const idlePolarSmoothedRef = useRef(0);
  const touchActiveRef = useRef(false);
  let t3;
  if ($[0] !== controlsRef) {
    t3 = (azimuth, polar) => {
      const controls = controlsRef.current;
      if (!controls) {
        return;
      }
      controls.setAzimuthalAngle(azimuth);
      controls.setPolarAngle(polar);
      controls.update();
    };
    $[0] = controlsRef;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const applyModelAngles = t3;
  let t4;
  if ($[2] !== baseAzimuth || $[3] !== basePolar || $[4] !== isMobileLayout) {
    t4 = () => {
      const polar_0 = isMobileLayout ? LANDING_HERO_ORBIT_TUNING.mobile : LANDING_HERO_ORBIT_TUNING.desktop;
      const pointerAzimuth = isMobileLayout ? touchAzimuthDeltaRef.current : pointerAzimuthDeltaRef.current;
      const pointerPolar = isMobileLayout ? touchPolarDeltaRef.current : pointerPolarDeltaRef.current;
      targetAzimuthRef.current = baseAzimuth + idleAzimuthSmoothedRef.current + pointerAzimuth;
      targetPolarRef.current = clamp(basePolar + idlePolarSmoothedRef.current + pointerPolar, polar_0.polarAngleMin, polar_0.polarAngleMax);
    };
    $[2] = baseAzimuth;
    $[3] = basePolar;
    $[4] = isMobileLayout;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const syncTargetFromDeltas = t4;
  let t5;
  if ($[6] !== applyModelAngles || $[7] !== baseAzimuth || $[8] !== basePolar) {
    t5 = () => {
      pointerAzimuthDeltaRef.current = 0;
      pointerPolarDeltaRef.current = 0;
      touchAzimuthDeltaRef.current = 0;
      touchPolarDeltaRef.current = 0;
      idleAzimuthSmoothedRef.current = 0;
      idlePolarSmoothedRef.current = 0;
      touchActiveRef.current = false;
      currentAzimuthRef.current = baseAzimuth;
      currentPolarRef.current = basePolar;
      targetAzimuthRef.current = baseAzimuth;
      targetPolarRef.current = basePolar;
      applyModelAngles(baseAzimuth, basePolar);
    };
    $[6] = applyModelAngles;
    $[7] = baseAzimuth;
    $[8] = basePolar;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const setModelRestingPosition = t5;
  let t6;
  if ($[10] !== syncTargetFromDeltas) {
    t6 = () => {
      pointerAzimuthDeltaRef.current = 0;
      pointerPolarDeltaRef.current = 0;
      syncTargetFromDeltas();
    };
    $[10] = syncTargetFromDeltas;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const resetPointerDeltas = t6;
  let t7;
  if ($[12] !== controlsRef || $[13] !== invertPointerX || $[14] !== isMobileLayout || $[15] !== syncTargetFromDeltas) {
    t7 = (clientX, clientY) => {
      if (isMobileLayout) {
        return;
      }
      if (!controlsRef.current || window.innerWidth === 0 || window.innerHeight === 0) {
        return;
      }
      const desktop = LANDING_HERO_ORBIT_TUNING.desktop;
      const xRatio = clientX / window.innerWidth - 0.5;
      const yRatio = clientY / window.innerHeight - 0.5;
      const xSign = invertPointerX ? -1 : 1;
      pointerAzimuthDeltaRef.current = -xSign * xRatio * desktop.maxAzimuthOffset * desktop.pointerAzimuthMultiplier;
      pointerPolarDeltaRef.current = yRatio * desktop.maxPolarOffset * desktop.pointerPolarMultiplier;
      syncTargetFromDeltas();
    };
    $[12] = controlsRef;
    $[13] = invertPointerX;
    $[14] = isMobileLayout;
    $[15] = syncTargetFromDeltas;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const updateModelFromPointer = t7;
  let t8;
  if ($[17] !== controlsRef || $[18] !== invertPointerX || $[19] !== isMobileLayout || $[20] !== syncTargetFromDeltas) {
    t8 = (clientX_0, clientY_0) => {
      if (!isMobileLayout || !controlsRef.current || window.innerWidth === 0 || window.innerHeight === 0) {
        return;
      }
      const mobile = LANDING_HERO_ORBIT_TUNING.mobile;
      const xRatio_0 = clientX_0 / window.innerWidth - 0.5;
      const yRatio_0 = clientY_0 / window.innerHeight - 0.5;
      const xSign_0 = invertPointerX ? -1 : 1;
      touchAzimuthDeltaRef.current = -xSign_0 * xRatio_0 * mobile.maxAzimuthOffset * mobile.touchAzimuthMultiplier;
      touchPolarDeltaRef.current = yRatio_0 * mobile.maxPolarOffset * mobile.touchPolarMultiplier;
      syncTargetFromDeltas();
    };
    $[17] = controlsRef;
    $[18] = invertPointerX;
    $[19] = isMobileLayout;
    $[20] = syncTargetFromDeltas;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const updateModelFromTouch = t8;
  let t9;
  if ($[22] !== setModelRestingPosition) {
    t9 = () => {
      setModelRestingPosition();
    };
    $[22] = setModelRestingPosition;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== isMobileLayout || $[25] !== setModelRestingPosition) {
    t10 = [isMobileLayout, setModelRestingPosition];
    $[24] = isMobileLayout;
    $[25] = setModelRestingPosition;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[27] !== applyModelAngles || $[28] !== idleMotionPhaseSec || $[29] !== isMobileLayout || $[30] !== resetPointerDeltas || $[31] !== syncTargetFromDeltas || $[32] !== updateModelFromPointer || $[33] !== updateModelFromTouch) {
    t11 = () => {
      const frameDamping = isMobileLayout ? LANDING_HERO_ORBIT_TUNING.mobile.frameDamping : LANDING_HERO_ORBIT_TUNING.desktop.frameDamping;
      let frameId = 0;
      const reduceMotionClient = prefersReducedMotion();
      const animateModel = () => {
        if (isMobileLayout) {
          const mobile_0 = LANDING_HERO_ORBIT_TUNING.mobile;
          if (!reduceMotionClient) {
            const timeSec = performance.now() / 1000 + idleMotionPhaseSec;
            const idleAzimuthTarget = Math.sin(timeSec * mobile_0.idleAzimuthAngularSpeed) * mobile_0.idleAzimuthAmplitudeRad + Math.sin(timeSec * mobile_0.idleAzimuthAngularSpeed * 1.73 + idleMotionPhaseSec * 2.1) * mobile_0.idleAzimuthAmplitudeRad * mobile_0.idleSecondaryAzimuthScale;
            const idlePolarTarget = Math.cos(timeSec * mobile_0.idlePolarAngularSpeed + 0.7) * mobile_0.idlePolarAmplitudeRad + Math.sin(timeSec * mobile_0.idlePolarAngularSpeed * 0.61 + idleMotionPhaseSec) * mobile_0.idlePolarAmplitudeRad * mobile_0.idleSecondaryPolarScale;
            idleAzimuthSmoothedRef.current = idleAzimuthSmoothedRef.current + (idleAzimuthTarget - idleAzimuthSmoothedRef.current) * mobile_0.idleSmoothing;
            idlePolarSmoothedRef.current = idlePolarSmoothedRef.current + (idlePolarTarget - idlePolarSmoothedRef.current) * mobile_0.idleSmoothing;
          }
          if (!touchActiveRef.current) {
            const decay = 1 - mobile_0.touchReleaseDecay;
            touchAzimuthDeltaRef.current = touchAzimuthDeltaRef.current * decay;
            touchPolarDeltaRef.current = touchPolarDeltaRef.current * decay;
            if (Math.abs(touchAzimuthDeltaRef.current) < TOUCH_DELTA_EPSILON && Math.abs(touchPolarDeltaRef.current) < TOUCH_DELTA_EPSILON) {
              touchAzimuthDeltaRef.current = 0;
              touchPolarDeltaRef.current = 0;
            }
          }
          syncTargetFromDeltas();
        }
        const nextAzimuth = currentAzimuthRef.current + (targetAzimuthRef.current - currentAzimuthRef.current) * frameDamping;
        const nextPolar = currentPolarRef.current + (targetPolarRef.current - currentPolarRef.current) * frameDamping;
        currentAzimuthRef.current = nextAzimuth;
        currentPolarRef.current = nextPolar;
        applyModelAngles(nextAzimuth, nextPolar);
        frameId = window.requestAnimationFrame(animateModel);
      };
      if (!isMobileLayout) {
        frameId = window.requestAnimationFrame(animateModel);
        const handleWindowMouseMove = event => {
          updateModelFromPointer(event.clientX, event.clientY);
        };
        const handlePointerExit = () => {
          resetPointerDeltas();
        };
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("blur", handlePointerExit);
        document.documentElement.addEventListener("mouseleave", handlePointerExit);
        return () => {
          window.removeEventListener("mousemove", handleWindowMouseMove);
          window.removeEventListener("blur", handlePointerExit);
          document.documentElement.removeEventListener("mouseleave", handlePointerExit);
          window.cancelAnimationFrame(frameId);
        };
      }
      frameId = window.requestAnimationFrame(animateModel);
      const syncTouchFromEvent = event_0 => {
        const primaryTouch = event_0.touches.item(0);
        if (!primaryTouch) {
          return;
        }
        updateModelFromTouch(primaryTouch.clientX, primaryTouch.clientY);
      };
      const handleTouchStart = event_1 => {
        touchActiveRef.current = true;
        syncTouchFromEvent(event_1);
      };
      const handleTouchMove = event_2 => {
        syncTouchFromEvent(event_2);
      };
      const handleTouchEnd = event_3 => {
        if (event_3.touches.length === 0) {
          touchActiveRef.current = false;
        } else {
          syncTouchFromEvent(event_3);
        }
      };
      const touchOptions = {
        passive: true,
        capture: true
      } as const;
      window.addEventListener("touchstart", handleTouchStart, touchOptions);
      window.addEventListener("touchmove", handleTouchMove, touchOptions);
      window.addEventListener("touchend", handleTouchEnd, touchOptions);
      window.addEventListener("touchcancel", handleTouchEnd, touchOptions);
      return () => {
        window.removeEventListener("touchstart", handleTouchStart, touchOptions);
        window.removeEventListener("touchmove", handleTouchMove, touchOptions);
        window.removeEventListener("touchend", handleTouchEnd, touchOptions);
        window.removeEventListener("touchcancel", handleTouchEnd, touchOptions);
        window.cancelAnimationFrame(frameId);
      };
    };
    t12 = [applyModelAngles, idleMotionPhaseSec, isMobileLayout, resetPointerDeltas, syncTargetFromDeltas, updateModelFromPointer, updateModelFromTouch];
    $[27] = applyModelAngles;
    $[28] = idleMotionPhaseSec;
    $[29] = isMobileLayout;
    $[30] = resetPointerDeltas;
    $[31] = syncTargetFromDeltas;
    $[32] = updateModelFromPointer;
    $[33] = updateModelFromTouch;
    $[34] = t11;
    $[35] = t12;
  } else {
    t11 = $[34];
    t12 = $[35];
  }
  useEffect(t11, t12);
};